/**
 * TypeScript log decorator:
 *  Add logging methods to class. Output in console contains name of class from which log is called;
 *  Add log to method calls. Output in console contains name of class, method, list of arguments and return value of the method;
 *  Add log to property access. Output contains name of class, property name and current value for getter, or new value for setter;
 *
 * Log decorators are enabled only if DEBUG variable set to `true` during compilation of this script;
 *   Run `grunt debug` to enable log decorators;
 *   Run `grunt build` to remove log decorators functionality;
 *
 * Log methods have exactly same functionality as console.log;
 * Log methods are extension of Logger specification https://console.spec.whatwg.org/#logger
 * How to format log messages https://console.spec.whatwg.org/#formatting-specifiers
 *
 * Log is disabled by default.
 * To enable it globally do following from entry point of application:
 *
 * @example enable log
 *   import log = com.egt.library.utils.log;
 *   log.isEnabled = true;
 *
 *   // Or put this before Main class
 *   window['egtLibrary'].log.isEnabled = true
 *
 * @example of a class decorator
 *   import * as log from '@egt/egt-library/dist/utils/log'
 *
 *   @log.decorator
 *   class SomeView {
 *       // Declare which methods from decorator to use in class.
 *       log;
 *
 *       someMethod(param) {
 *          this.log('Some method called with param[%s]', param);
 *          // Output to console: [EGT][SomeView] Some method called with param[trololo]
 *       }
 *   }
 *
 * @example of a method decorator
 *   import * as log from '@egt/egt-library/dist/utils/log'
 *
 *   class SomeView {
 *       @log.decorator
 *       someMethod(param1, param2) {
 *          return 'Wanna get some sleep';
 *       }
 *   }
 *
 *   const view = new SomeView();
 *   view.someMethod('friday', 'afternoon');
 *   // Output to console: [EGT][SomeView] SomeView.someMethod('friday', 'afternoon') => 'Wanna get some sleep'
 *
 * @example of a property decorator
 *   import * as log from '@egt/egt-library/dist/utils/log'
 *
 *   class SomeView {
 *       @log.decorator
 *       public someProperty: number = 10;
 *   }
 *
 *   const view = new SomeView();
 *   view.someProperty;
 *   // Output to console: [EGT][SomeView] Get SomeView.someProperty => 10;
 *
 *   view.someProperty = 21;
 *   // Output to console: [EGT][SomeView] Set SomeView.someProperty => 21;
 *
 * @example direct use of log, warn or error functions
 *   import log = com.egt.library.utils.log;
 *   import warn = com.egt.library.utils.warn;
 *   import error = com.egt.library.utils.warn;
 *
 *   log('Q: How can you tell if a poker player is bluffing? A: %s', answer);
 *   // Output in console: [EGT][log] How can you tell if a poker player is bluffing? A: His chips are moving.
 *
 *   warn('I sense a trap.');
 *   // Output marked as warning in console: [EGT][warn] I sense a trap.
 *
 *   error('Spring the trap.');
 *   // Output marked as error in console: [EGT][error] Spring the trap.
 */

// Set by grunt build script to `true` for debug and to `false` for release.
declare var DEBUG: boolean;

// Log functions
export const log: Function = LogFunction('log');
export const warn: Function = LogFunction('warn');
export const error: Function = LogFunction('error');

// Global log settings
export let isEnabled: boolean = false;
export let isStylized: boolean = false;

export class Config {
    style?: string;
    isEnabled?: boolean = false
    isStylized?: boolean = false;
}

export function decorator(...args: any[]) {
    switch (args.length) {
        case 1:
            return logClass.apply(this, args);
        case 2:
            return logProperty.apply(this, args);
        case 3:
            if (typeof args[2] === 'number') {
                return logParameter.apply(this, args);
            } else if (typeof args[2] === 'undefined') {
                return logProperty.apply(this, args);
            }
            return logMethod.apply(this, args);
        default:
            throw new Error('Decorators are not valid here!');
    }
}

// TODO create factory for config decorator.
export function decoratorConfig(config: any): Function {
    return (target: Function) => logClass(target, config as Config);
}

// Implemented log methods.
const logMethods = ['log', 'warn', 'error'];

let logClass: Function;
let logMethod: Function;
let logProperty: Function;
let logParameter: Function;

function noop() {
}

function LogFunction(level: string): Function {
    return (...args) => {
        output(level, level, ...args);
    }
}

function output(target, level, ...args) {
    if (typeof args[0] === 'string') {
        args[0] = `[EGT][${target}] ` + args[0];
    } else {
        args.unshift(`[EGT][${target}]`);
    }

    console[level](...args);
}

function outputStylized(instance, level, style, ...args) {
    if (typeof args[0] === 'string') {
        args[0] = `[EGT][%c${instance}%c] ` + args[0];
        // Empty string is a style for second %c flag,
        // which is there only to terminate first style.
        args.splice(1, 0, style, '');
    } else {
        args.unshift(`[EGT][%c${instance}`, style, ']');
    }

    console[level](...args);
}

// When toString method called on class - output class name.
function addToStringMethod(prototype) {
    if (prototype && !prototype.hasOwnProperty('toString')) {
        Object.defineProperty(prototype, 'toString', {
            value: function() {
                return this.constructor['name'];
            }
        });
    }
}

function stringify(o) {
    return JSON.stringify(o, function (k, v) { return k ? '' + v : v; });
}

if (DEBUG) {
    logClass = function(target: Function, config: Config): Function {
        const constructorName = target.prototype.constructor['name'];
        const objectProperties = {};
        let isForceEnabled: boolean = false;
        let isForceDisabled: boolean = false;
        let isForceStylizedEnabled: boolean = false;
        let isForceStylizedDisabled: boolean = false;
        let style;

        if (config) {
            if (typeof config.style === 'string') {
                style = config.style;
            }

            if (typeof config.isEnabled === 'boolean') {
                isForceEnabled = config.isEnabled ? true : false;
                isForceDisabled = !isForceEnabled;
            }

            if (typeof config.isStylized === 'boolean') {
                isForceStylizedEnabled = config.isStylized ? true : false;
                isForceStylizedDisabled = !isForceStylizedEnabled;
            }
        }

        if (typeof style !== 'string') {
            if (/View$/.test(constructorName)) {
                style = 'color: #41BBD9;';
            } else if (/Model$/.test(constructorName)) {
                style = 'color: #99C24D;';
            } else if (/Controller$/.test(constructorName)) {
                style = 'color: #F18F01;';
            }
        }

        logMethods.forEach(level => {
            objectProperties[level] = {
                value: function(...args) {
                    if (isForceEnabled || (isEnabled && !isForceDisabled)) {
                        if (isForceStylizedEnabled || (isStylized && !isForceStylizedDisabled)) {
                            outputStylized(this, level, style, ...args);
                        } else {
                            output(this, level, ...args);
                        }
                    }
                }
            };
        });

        Object.defineProperties(target.prototype, objectProperties);

        addToStringMethod(target.prototype);

        return target;
    }

    // TODO check isEnabled and isStylized
    logProperty = function(target: any, key: string) {
        let value = this[key];

        const getter = function() {
            output(target, 'log', `Get ${target}.${key} => ${value}`);
            return value;
        };

        const setter = function(newValue) {
            output(target, 'log', `Set ${target}.${key} => ${newValue}`);
            value = newValue;
        };

        if (delete this[key]) {
            Object.defineProperty(target, key, {
                get: getter,
                set: setter,
                enumerable: true,
                configurable: true
            });
        }
    }

    // TODO check isEnabled and isStylized
    logMethod = function(target: Function, key: string, descriptor: any) {
        addToStringMethod(target);

        var originalMethod = descriptor.value;

        descriptor.value = function(...args: any[]) {
            let resultString: string;
            const argsString: string = args.map(a => {
                try {
                    return stringify(a);
                } catch (e) {
                    // output(log, 'warn', e);
                    return String(a);
                }
            }).join(', ');

            const label = `${this}.${key}(${argsString})`;

            output(this, 'log', `Call: ${label}`);

            const result = originalMethod.apply(this, args);

            try {
                resultString = stringify(result);
            } catch (e) {
                // output(log, 'warn', e);
                resultString = String(result);
            }

            output(this, 'log', `Result: ${label} => ${resultString}`);

            return result;
        }

        return descriptor;
    }

    // TODO make it usable. Store parameter name and use it when logging method call.
    // TODO check isEnabled and isStylized
    logParameter = function(target: any, key: string, index: number) {
        output(target, 'log', `${target}.${key}(${index})`);
    }
} else {
    // In release mode add empty methods instead of log calls.
    logClass = function logClass(target: Function, config: Config): Function {
        const objectProperties = {};

        logMethods.forEach(level => {
            objectProperties[level] = {value: noop};
        });

        Object.defineProperties(target.prototype, objectProperties);

        return target
    };

    // In release mode do not modify properties.
    logProperty = noop;

    // In release mode do not modify methods.
    logMethod = function(target: Function, key: string, descriptor: any) {
        return descriptor;
    };

    logParameter = noop;
}
